/**
 * 파일명: business.js
 * 작성자: 김준형
 * 작성일자: 26.08.10
 * 설명: Landing - 주요 사업 영역 
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import style from "css/landing.module.css";

// Smart Space 
const SPACE_SLIDES = ["space_1", "space_2", "space_3"];
const SPACE_SLIDE_DELAY = 4000;

// Smart Space 리스트
const SPACE_LIST = [
  { name: "하이브리드 오피스", link: "smart-space/work" },
  { name: "스마트 강의실", link: "smart-space/room" },
  { name: "텔레헬스 시스템", link: "smart-space/telehealth" },
];

// Smart Solution 
const MEETING_SOLUTION_LIST = [
  { name: "DeviceOne", link: "smart-solution/deviceOne" },
  { name: "Be Gate", link: "smart-solution/begate" },
  { name: "CMS Manager", link: "smart-solution/cmsManager" },
  {
    name: "Private Connect Manager",
    link: "smart-solution/privateConnectManager",
  },
];

// Smart Device
const DEVICE_LIST = [
  { name: "Bar Generation 2", link: "smart-device/neat/bar2" },
  { name: "Bar Pro", link: "smart-device/neat/barPro" },
  { name: "Board 50", link: "smart-device/neat/board50" },
  { name: "Board Pro", link: "smart-device/neat/boardPro" },
  // Neat Board 32 전용 라우트가 없어 목록 페이지로 연결. 추후 수정 필요.
  { name: "Board 32", link: "smart-device/neat/boardPro" },
  { name: "Center", link: "smart-device/neat/center" },
  { name: "Pad", link: "smart-device/neat/pad" },
];

function Business() {
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const [spaceIdx, setSpaceIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSpaceIdx((prev) => (prev + 1) % SPACE_SLIDES.length);
    }, SPACE_SLIDE_DELAY);

    return () => clearInterval(timer);
  }, [spaceIdx]);

  /*
    대각선 화살표
  */
  const arrowIcon = (
    <span
      className={style.business_list_arrow}
      aria-hidden="true"
    />
  );

  return (
    <div className={style.business_wrap}>
      <div className={style.business_head}>
        <span className={style.business_badge}>What to do</span>
        <h2 className={style.business_title}>주요 사업 영역</h2>
        <p className={style.business_subtitle}>
          공간과 기술을 설계하는 리브테크놀로지의 핵심 역량
        </p>
      </div>

      {/* Smart Space */}
      <div className={`${style.business_row} ${style.business_row_reverse}`}>
        <div className={style.business_visual}>
          {SPACE_SLIDES.map((slide, index) => (
            <img
              key={slide}
              className={`${style.business_visual_img} ${
                index === spaceIdx ? style.business_on : ""
              }`}
              src={`${process.env.REACT_APP_CLIENT_URL}img/landing/business/${slide}${
                isMobile ? "_m" : ""
              }.png`}
              alt="맞춤형 공간 설계"
            />
          ))}
          <div className={style.business_indicator}>
            {SPACE_SLIDES.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={
                  index === spaceIdx
                    ? style.business_indicator_bar
                    : style.business_indicator_dot
                }
                onClick={() => setSpaceIdx(index)}
                aria-label={`${index + 1}번째 이미지 보기`}
              />
            ))}
          </div>
        </div>

        <div className={style.business_card}>
          <div className={style.business_card_label}>Smart Space</div>
          <div className={style.business_card_title}>
            환경에 최적화된{" "}
            <span className={style.business_point}>맞춤형 공간 설계</span>
          </div>
          <div className={style.business_card_desc}>
            {isMobile ? (
              "하이브리드 오피스 · 스마트 강의실 · 텔레헬스 시스템 등 사용 목적과 동선을 고려한 물리적 공간 인프라를 구축합니다."
            ) : (
              <>
                하이브리드 오피스 · 스마트 강의실 · 텔레헬스 시스템 등 사용
                목적과 동선을 고려한
                <br />
                물리적 공간 인프라를 구축합니다.
              </>
            )}
          </div>
          <div className={style.business_list}>
            {SPACE_LIST.map((item) => (
              <Link
                key={item.link}
                className={style.business_list_item}
                to={`${process.env.REACT_APP_CLIENT_URL}${item.link}`}
              >
                <span className={style.business_list_dot} />
                <span className={style.business_list_text}>{item.name}</span>
                {arrowIcon}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Smart Solution */}
      <div className={style.business_row}>
        <div className={style.business_visual}>
          <img
            className={`${style.business_visual_img} ${style.business_on}`}
            src={`${process.env.REACT_APP_CLIENT_URL}img/landing/business/solution${
              isMobile ? "_m" : ""
            }.png`}
            alt="리브의 6대 솔루션"
          />
        </div>

        <div className={style.business_card}>
          <div className={style.business_card_label}>Smart Solution</div>
          <div className={style.business_card_title}>
            하이브리드 워킹을 위한{isMobile ? <br /> : " "}
            <span className={style.business_point}>리브의 6대 솔루션</span>
          </div>
          <div className={style.business_card_desc}>
            기업의 업무 환경을 최적화하는 6대 핵심 솔루션을 통해 스마트워크의
            새로운 기준을 제시합니다.
          </div>

          <div className={style.business_group}>
            <div className={style.business_group_row}>
              <div className={style.business_group_box}>
                <span className={style.business_group_chip}>
                  스마트 워크 플레이스
                </span>
                <Link
                  className={style.business_list_item}
                  to={`${process.env.REACT_APP_CLIENT_URL}smart-solution/allplace`}
                >
                  <span className={style.business_list_dot} />
                  <span className={style.business_list_text}>all Place</span>
                  {arrowIcon}
                </Link>
              </div>
              <div className={style.business_group_box}>
                <span className={style.business_group_chip}>
                  텔레헬스 솔루션
                </span>
                <Link
                  className={style.business_list_item}
                  to={`${process.env.REACT_APP_CLIENT_URL}smart-solution/mediLink`}
                >
                  <span className={style.business_list_dot} />
                  <span className={style.business_list_text}>MediLink</span>
                  {arrowIcon}
                </Link>
              </div>
            </div>

            <div className={style.business_group_box}>
              <span className={style.business_group_chip}>
                화상회의 운영 솔루션
              </span>
              <span className={style.business_group_divider} />
              <div className={style.business_list_2col_flow}>
                {MEETING_SOLUTION_LIST.map((item) => (
                  <Link
                    key={item.link}
                    className={style.business_list_item}
                    to={`${process.env.REACT_APP_CLIENT_URL}${item.link}`}
                  >
                    <span className={style.business_list_dot} />
                    <span className={style.business_list_text}>
                      {item.name}
                    </span>
                    {arrowIcon}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Smart Device */}
      <div className={`${style.business_row} ${style.business_row_reverse}`}>
        <div className={style.business_visual}>
          <img
            className={`${style.business_visual_img} ${style.business_on}`}
            src={`${process.env.REACT_APP_CLIENT_URL}img/landing/business/device${
              isMobile ? "_m" : ""
            }.png`}
            alt="AI 화상회의 장비"
          />
        </div>

        <div className={style.business_card}>
          <div className={style.business_card_label}>Smart Device</div>
          <div
            className={`${style.business_card_title} ${style.business_card_title_device}`}
          >
            소통 품질을 높이는{" "}
            <span className={style.business_point}>
              AI 화상회의{isMobile ? <br /> : " "}장비
            </span>
          </div>
          <div className={style.business_card_desc}>
            AI의 기술력이 담긴 Neat로 공간의 규모와 목적에 가장 적합한 최신
            화상회의 및 협업을 연결합니다.
          </div>
          <div className={style.business_list_2col}>
            {DEVICE_LIST.map((item) => (
              <Link
                key={item.name}
                className={style.business_list_item}
                to={`${process.env.REACT_APP_CLIENT_URL}${item.link}`}
              >
                <span className={style.business_list_dot} />
                <span className={style.business_list_text}>
                  <span className={style.business_neat_prefix}>Neat</span>{" "}
                  {item.name}
                </span>
                {arrowIcon}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Business;
